import type { UserInitModel } from '~/common/models/api.models';

export interface UseEmailReturn {
  email: Ref<string>;
  isLoading: Ref<boolean>;
  showInputError: Ref<boolean>;
  checkEmailFormat: (email: string) => boolean;
  useCheckDomainExists: (email: string) => Promise<boolean>;
  validateEmail: (withRedirect?: boolean) => Promise<boolean>;
  initUser: (withRedirect: boolean, inputEmail?: string) => Promise<boolean>;
  registerPayerWithoutEmail: () => Promise<void>;
  setEmailAfterPpPayment: (email: string) => Promise<void>;
}

export function useEmail(errorCallBack?: any): UseEmailReturn {
  const { locale } = useI18n();
  const store = useKegelAnalyticStore();
  const gatewayStore = useKegelGatewayStore();
  const API = useKegelApi();
  const route = useRoute();
  const { openError } = useAllModals();
  const device = useDevice();
  const { getGtagParams } = useCustomGtag();
  const { track } = useEventsDispatcher();
  const { getAnalyticsAttributes } = useMixpanel();
  const { getHashedText } = useHelpers();
  const { $logger, $paymentMethods } = useNuxtApp();
  const email = ref<string>('');
  const gaUserId = ref('');
  const gaGbraid = ref('');
  const gaGclid = ref('');
  const gaWbraid = ref('');
  const isLoading = ref(false);
  const showInputError = ref(false);
  const { initLogRocket } = useLogRocket();

  const { checkEmailFormat, checkDomainExists } = useEmailValidation();
  const { trackLeadForEmail, trackPaymentEmailRetrieved } = useEmailTracking();
  const { pixelId } = useFacebookPixel();
  const ageItems = useAppConfig().homePage;

  onMounted(async () => {
    try {
      await setGTMparams();
      await nextTick();
      store.analyticsAttributions = await getAnalyticsAttributes(route.query.rtkcid as string);
    } catch (error: unknown) {
      $logger.note({
        category: 'email',
        message: 'loading attribution failed',
        level: 'warning',
        cause: error,
      });
    }
  });

  onBeforeUnmount(() => {
    isLoading.value = false;
  });

  const emailPayload = computed<UserInitModel>(() => {
    let domain: string | undefined;
    if (import.meta.client) {
      domain = globalThis.location.hostname;
    }

    const cookieClickId = useCookie('rtkclickid-store', {
      domain,
      path: '/',
      maxAge: 86400 * 90 * 1000,
    });

    const uuid_c1 = useCookie('_scid', {
      domain,
      path: '/',
      maxAge: 86400 * 90 * 1000,
    });

    const savedAgeId = String(store.ageId ?? '');
    const age = ageItems?.find(item => String(item.id) === savedAgeId) ?? null;
    const age_group = age?.group;

    const goal = store.goals?.main?.replace(/^goal_/, '').replaceAll('_', '-');
    const level = store.startLevel || undefined;

    return {
      email: email.value,
      platform: device?.isIos ? 'ios' : 'android',
      ga_user_id: gaUserId.value,
      ga_gbraid: gaGbraid.value,
      ga_gclid: gaGclid.value,
      ga_wbraid: gaWbraid.value,
      fbp: useCookie('_fbp').value,
      fbc: useCookie('_fbc').value || store.analyticsAttributions?.fbclid,
      ad_id: store.analyticsAttributions?.ad || (route.query.sub1 as string),
      ad_placement: store.analyticsAttributions?.ad_placement || (route.query.sub7 as string),
      ad_source: store.analyticsAttributions?.utm_source || (route.query.utm_source as string),
      click_id: store.clickId || String(cookieClickId.value),
      click_time: store.analyticsAttributions?.click_time || '',
      tiktok_click_id: route.query.ttclid as string,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      pixel: pixelId,
      twclid: route.query.twclid as string,
      snapchat_click_id: route.query.ScCid as string,
      snapchat_uuid_c1: uuid_c1.value as string,
      language: locale.value as string,
      age_group,
      ...(goal ? { goal } : {}),
      ...(level ? { level } : {}),
    };
  });

  async function setGTMparams() {
    try {
      const result = await getGtagParams();
      gaUserId.value = result?.ga_user_id;
      gaGbraid.value = result?.ga_gbraid;
      gaGclid.value = result?.ga_gclid;
      gaWbraid.value = result?.ga_wbraid;
    } catch (error: unknown) {
      $logger.note({
        category: 'email',
        message: 'reading gtag params failed',
        level: 'warning',
        cause: error,
      });
    }
  }

  async function useCheckDomainExists(emailValue: string): Promise<boolean> {
    return checkDomainExists(emailValue);
  }

  async function validateEmail(withRedirect = true): Promise<boolean> {
    isLoading.value = true;

    const isValidDomain = await useCheckDomainExists(email.value);

    if (!isValidDomain) {
      if (errorCallBack) {
        errorCallBack();
      } else {
        openError({ content: 'mail_error_message' });
      }
      showInputError.value = true;
      isLoading.value = false;
      return false;
    }

    const ok = await initUser(withRedirect);
    if (ok) {
      track('E-mail Screen Completed', { email: email.value });
    }
    return ok;
  }

  function warmUpCheckoutSdk(): void {
    if (gatewayStore.provider !== 'round') {
      return;
    }
    void useHyperswitchCheckout().warmHyperswitchSdk();
  }

  async function initUser(withRedirect: boolean, inputEmail?: string): Promise<boolean> {
    try {
      if (inputEmail) {
        email.value = inputEmail;
      }

      await Promise.all([
        API.initUser(emailPayload.value),
        API.getPaymentConfig(),
      ]);

      warmUpCheckoutSdk();

      store.userEmail = email.value;

      const hashedEmail = await getHashedText(email.value);
      store.sha256UserEmail = hashedEmail;

      await trackLeadForEmail(email.value, hashedEmail);
      await initLogRocket();

      return true;
    } catch (error: unknown) {
      $logger.error(error as Error, {
        tags: {
          flow: 'email',
          step: 'init_user',
        },
        extra: {
          withRedirect,
          path: route.path,
        },
      });

      isLoading.value = false;
      return false;
    }
  }

  async function registerPayerWithoutEmail() {
    try {
      const payload = emailPayload.value;
      delete payload.email;
      await API.initUser(payload);
    } catch (error: unknown) {
      $logger.error(error as Error, {
        tags: {
          flow: 'email',
          step: 'register_payer_without_email',
          payment_method: $paymentMethods?.paypal,
        },
        extra: {
          path: route.path,
        },
      });

      isLoading.value = false;
    }
  }

  async function setEmailAfterPpPayment(emailValue: string) {
    store.userEmail = emailValue;

    const hashedEmail = await getHashedText(emailValue);
    store.sha256UserEmail = hashedEmail;

    await trackPaymentEmailRetrieved(emailValue);
  }

  return {
    checkEmailFormat,
    useCheckDomainExists,
    validateEmail,
    email,
    initUser,
    showInputError,
    registerPayerWithoutEmail,
    setEmailAfterPpPayment,
    isLoading,
  };
}
